import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { Grid } from "./Grid.js";
import { InputHandler } from "./InputHandler.js";
import { GameLogic } from "./GameLogic.js";
import { UIManager } from "./UIManager.js";
import { TitleScreen } from "./TitleScreen.js";
import { SoundManager } from "./SoundManager.js";
import { BackgroundShader } from "./BackgroundShader.js";
import gsap from "gsap";

export class ChakrasGame {
  constructor(container) {
    if (!container) {
      throw new Error("Container element is required!");
    }
    this.container = container;
    this.clock = new THREE.Clock();
    this.soundManager = new SoundManager();
    this.debugMode = false;
    this.gameStarted = false;
    this.orbitControlsEnabled = false;
    this.cameraTransitionComplete = false;
    this.init();
  }

  init() {
    this.initThree();
    this.initGame();

    // Create title screen after game is initialized
    this.titleScreen = new TitleScreen(this.container, this);
    this.titleScreen.setStartGameCallback(() => this.startNewGame());

    // Show title screen
    this.titleScreen.show();
  }

  startNewGame(skipCameraTransition = false, initialLayout = null) {
    this.gameStarted = true;

    // Reset game state
    if (this.gameLogic) {
      // If we have an initial layout from the tutorial, use it instead of generating a new one
      this.gameLogic.startLevel(1, initialLayout);

      // Only show tutorial gesture if not coming from tutorial and UI manager is ready
      if (
        (!this.titleScreen || !this.titleScreen.isTutorialActive()) &&
        this.uiManager &&
        this.uiManager.container && // Add check for container
        typeof this.uiManager.showTutorialGesture === "function"
      ) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              this.uiManager.showTutorialGesture();
            } catch (error) {
              console.error("Error showing tutorial gesture:", error);
            }
          }, 1000);
        });
      }
    }

    // Enable input handling
    if (this.inputHandler) {
      this.inputHandler.enabled = true;
    }

    // Start playing background music
    if (this.soundManager) {
      this.soundManager.playBackgroundMusic();
    }

    // Skip camera transition if we're coming from tutorial or if transition is already complete
    if (skipCameraTransition || this.cameraTransitionComplete) {
      // Just make sure the camera is in the right position
      this.updateCameraForScreenSize();
      return;
    }

    // Calculate camera position based on screen size
    const isMobile = window.innerWidth <= 768;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortrait = height > width;

    // Calculate the required camera height to maintain consistent board size
    let hitAreaWorldSize = 7 * 1.2 + 0.6; // Grid size plus padding

    // Add more padding for mobile devices
    if (isMobile) {
      // Increase padding for mobile to ensure the board is smaller than the viewport
      hitAreaWorldSize = 7 * 1.0 + 0.6; // Further reduced spacing for mobile
    }

    const topUIHeight = isPortrait ? 160 : 60;
    const bottomUIHeight = 80;
    const availableHeight = height - topUIHeight - bottomUIHeight;
    const availableAspect = width / availableHeight;

    // Initialize variables for camera settings
    let cameraDistance;
    let targetY;

    if (isMobile) {
      // Calculate FOV and distance for mobile to ensure grid fits
      const targetFov = 65; // Even wider FOV for mobile
      this.camera.fov = targetFov;

      // Calculate required distance for the grid to fit
      const vFov = (targetFov * Math.PI) / 180;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * availableAspect);
      const distanceVertical = hitAreaWorldSize / 2 / Math.tan(vFov / 2);
      const distanceHorizontal = hitAreaWorldSize / 2 / Math.tan(hFov / 2);

      // Add more margin for mobile devices (35% instead of 20%)
      cameraDistance = Math.max(distanceVertical, distanceHorizontal) * 1.35;
      targetY = cameraDistance * 0.8;

      // Set initial tilted view position
      this.camera.position.set(0, targetY, cameraDistance * 0.6);
    } else {
      // Desktop settings remain unchanged
      cameraDistance = 15;
      targetY = cameraDistance;
      this.camera.position.set(0, 15, 10);
      this.camera.fov = 60;
    }
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();

    // Animate to overhead view
    gsap.to(this.camera.position, {
      x: 0,
      y: targetY,
      z: 0,
      duration: 1.5,
      ease: "power2.inOut",
      onUpdate: () => {
        this.camera.lookAt(0, 0, 0);
        if (this.controls) {
          this.controls.update();
        }
      },
    });

    // Set the flag to indicate camera transition is complete
    this.cameraTransitionComplete = true;
  }

  initThree() {
    // Scene setup
    this.scene = new THREE.Scene();

    // Create background shader
    const backgroundGeometry = new THREE.PlaneGeometry(2, 2);
    const backgroundMaterial = new THREE.ShaderMaterial({
      uniforms: BackgroundShader.uniforms,
      vertexShader: BackgroundShader.vertexShader,
      fragmentShader: BackgroundShader.fragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });
    const backgroundMesh = new THREE.Mesh(
      backgroundGeometry,
      backgroundMaterial
    );
    backgroundMesh.frustumCulled = false;
    this.backgroundMesh = backgroundMesh;

    // Create a separate scene and camera for the background
    this.backgroundScene = new THREE.Scene();
    this.backgroundCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.backgroundScene.add(backgroundMesh);

    // Camera setup
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 1000);

    // Set initial camera position based on screen size
    this.updateCameraForScreenSize();

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      alpha: true,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    this.renderer.autoClear = false;
    this.container.appendChild(this.renderer.domElement);

    // Update background shader uniforms
    BackgroundShader.uniforms.iResolution.value.set(
      window.innerWidth,
      window.innerHeight
    );

    // Post processing setup
    this.initPostProcessing();

    // Lighting setup
    this.setupLighting();

    // Set up controls but keep them disabled
    this.setupControls();
    if (this.controls) {
      this.controls.enabled = false;
    }

    // Handle window resize and orientation change
    window.addEventListener("resize", () => this.handleResize(), false);
    window.addEventListener(
      "orientationchange",
      () => this.handleResize(),
      false
    );
  }

  setupLighting() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    this.scene.add(directionalLight);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x8888ff, 0.5);
    this.scene.add(hemisphereLight);
  }

  setupControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 3;
    this.controls.minPolarAngle = 0;
    this.updateControlsForScreenSize();
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: null,
      RIGHT: null,
    };
    this.controls.enablePan = false;
    this.controls.target.set(0, 0, 0);
    this.controls.enabled = false;
    this.controls.update();
  }

  updateCameraForScreenSize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const aspect = width / height;
    const isPortrait = height > width;
    const isMobile = width <= 768;

    this.camera.aspect = aspect;

    // Calculate the required camera height to maintain consistent board size
    // Use hit area size (7 cells * 1.2 spacing) plus padding for visual clarity
    let hitAreaWorldSize = 7 * 1.2 + 0.6; // Add half a spacing unit for edge hit areas

    // Add more padding for mobile devices
    if (isMobile) {
      // Increase padding for mobile to ensure the board is smaller than the viewport
      hitAreaWorldSize = 7 * 1.0 + 0.6; // Further reduced spacing for mobile
    }

    // Adjust FOV based on screen size and UI space
    const topUIHeight = isPortrait ? 160 : 60; // Height of top UI in pixels
    const bottomUIHeight = 80; // Height of bottom UI in pixels
    const availableHeight = height - topUIHeight - bottomUIHeight;
    const availableAspect = width / availableHeight;

    // Calculate FOV to fit the board in the available space
    const targetFov = isPortrait ? 75 : 70;
    const adjustedFov =
      2 *
      Math.atan(
        Math.tan((targetFov * Math.PI) / 180 / 2) * (height / availableHeight)
      ) *
      (180 / Math.PI);

    this.camera.fov = adjustedFov;

    // Calculate distances needed for both vertical and horizontal constraints
    const vFov = (adjustedFov * Math.PI) / 180; // vertical FOV in radians
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * availableAspect); // horizontal FOV in radians

    // Calculate distance needed for vertical fit
    const distanceVertical = hitAreaWorldSize / 2 / Math.tan(vFov / 2);
    // Calculate distance needed for horizontal fit
    const distanceHorizontal = hitAreaWorldSize / 2 / Math.tan(hFov / 2);

    // Use the larger distance to ensure hit areas fit in both dimensions
    // Add more margin for mobile devices
    const marginFactor = isMobile ? 1.35 : 1.1; // 35% margin for mobile, 10% for desktop
    const distance =
      Math.max(distanceVertical, distanceHorizontal) * marginFactor;

    if (this.gameStarted) {
      // If game has started, maintain overhead view
      this.camera.position.set(0, distance, 0);
    } else {
      // Initial tilted view before game starts
      if (width < 768 || isPortrait) {
        this.camera.position.set(0, distance * 0.8, distance * 0.6);
      } else {
        const heightRatio = 8 / 5;
        const height = distance * 0.8;
        const depth = height / heightRatio;
        this.camera.position.set(0, height, depth);
      }
    }

    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  updateControlsForScreenSize() {
    if (!this.controls) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortrait = height > width;

    if (width < 768 || isPortrait) {
      this.controls.minDistance = isPortrait ? 10 : 8;
      this.controls.maxDistance = isPortrait ? 18 : 15;
    } else {
      this.controls.minDistance = 3.5;
      this.controls.maxDistance = 10;
    }

    this.controls.update();
  }

  handleResize() {
    // Get current dimensions
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Update camera
    this.updateCameraForScreenSize();

    // Update controls if they exist
    this.updateControlsForScreenSize();

    // Update renderer and composer
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);

    // Update background shader uniforms
    BackgroundShader.uniforms.iResolution.value.set(
      width * this.renderer.getPixelRatio(),
      height * this.renderer.getPixelRatio()
    );

    // Update post-processing
    const bloomPass = this.composer.passes.find(
      (pass) => pass instanceof UnrealBloomPass
    );
    if (bloomPass) {
      bloomPass.resolution.set(width, height);
    }

    // Force a render to prevent blank frames
    this.composer.render();
  }

  initPostProcessing() {
    // Create composer
    this.composer = new EffectComposer(this.renderer);

    // Add render pass for the background
    const bgRenderPass = new RenderPass(
      this.backgroundScene,
      this.backgroundCamera
    );
    bgRenderPass.clear = true;
    this.composer.addPass(bgRenderPass);

    // Add render pass for the game scene
    const renderPass = new RenderPass(this.scene, this.camera);
    renderPass.clear = false;
    this.composer.addPass(renderPass);

    // Add bloom pass with refined settings for more balanced bloom
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.2,
      0.4,
      0.12
    );

    // Fine-tune bloom settings for better color balance
    bloomPass.threshold = 0.12;
    bloomPass.strength = 1.2;
    bloomPass.radius = 0.4;
    this.composer.addPass(bloomPass);
  }

  initGame() {
    // Initialize components in the correct order
    this.grid = new Grid(this.scene);
    this.grid.setSoundManager(this.soundManager);
    this.uiManager = new UIManager();
    this.gameLogic = new GameLogic(this.scene, this.grid, this.uiManager);

    // Set up cross-references
    this.uiManager.setGameLogic(this.gameLogic);
    this.uiManager.setGame(this);
    this.uiManager.setSoundManager(this.soundManager);

    // Set up level progression
    this.uiManager.setLevelCompleteCallback(() => {
      this.gameLogic.nextLevel();
    });

    // Initialize input handler with all required dependencies
    this.inputHandler = new InputHandler(
      this.camera,
      this.renderer.domElement,
      this.grid,
      this.gameLogic
    );
    this.gameLogic.setInputHandler(this.inputHandler);
    this.inputHandler.enabled = true;

    // Pass orbit controls and game reference to input handler
    this.inputHandler.setOrbitControls(this.controls);
    this.inputHandler.setGame(this);

    // Pass sound manager to input handler
    this.inputHandler.setSoundManager(this.soundManager);

    // Start first level
    this.gameLogic.startLevel(1);
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();
    const elapsedTime = this.clock.getElapsedTime();

    // Update background shader time
    BackgroundShader.uniforms.iTime.value = elapsedTime;

    // Update controls if enabled
    if (this.controls && this.controls.enabled) {
      this.controls.update();
    }

    // Update game logic and animations
    if (this.inputHandler) {
      this.inputHandler.update(elapsedTime);
    }

    // Update crystal animations
    if (this.grid) {
      this.grid.update();
    }

    // Render everything through the composer
    this.composer.render();
  }

  toggleOrbitControls(enabled) {
    if (!this.controls) {
      this.setupControls();
    }

    this.orbitControlsEnabled = enabled;
    this.controls.enabled = enabled;

    // Log camera state when enabling orbit controls
    if (enabled) {
      const currentRotation = new THREE.Euler().setFromQuaternion(
        this.camera.quaternion
      );
      console.log("=== Enabling Orbit Controls ===");
      console.log("Camera state before orbit:", {
        position: {
          x: this.camera.position.x.toFixed(2),
          y: this.camera.position.y.toFixed(2),
          z: this.camera.position.z.toFixed(2),
        },
        rotation: {
          x: THREE.MathUtils.radToDeg(currentRotation.x).toFixed(2),
          y: THREE.MathUtils.radToDeg(currentRotation.y).toFixed(2),
          z: THREE.MathUtils.radToDeg(currentRotation.z).toFixed(2),
        },
      });
    }

    // If disabling controls, smoothly animate back to overhead view
    if (!enabled && this.gameStarted) {
      const currentDistance = this.camera.position.length();

      // Get current camera state
      const startPos = this.camera.position.clone();
      const startQuaternion = this.camera.quaternion.clone();

      // Log the state when disabling orbit controls
      const currentRotation = new THREE.Euler().setFromQuaternion(
        startQuaternion
      );
      console.log("=== Disabling Orbit Controls ===");
      console.log("Camera state after orbit:", {
        position: {
          x: startPos.x.toFixed(2),
          y: startPos.y.toFixed(2),
          z: startPos.z.toFixed(2),
        },
        rotation: {
          x: THREE.MathUtils.radToDeg(currentRotation.x).toFixed(2),
          y: THREE.MathUtils.radToDeg(currentRotation.y).toFixed(2),
          z: THREE.MathUtils.radToDeg(currentRotation.z).toFixed(2),
        },
      });

      // Create target quaternion for the final overhead view (looking straight down)
      const targetQuaternion = new THREE.Quaternion();
      const targetEuler = new THREE.Euler(-Math.PI / 2, 0, 0, "XYZ");
      targetQuaternion.setFromEuler(targetEuler);

      // Log target rotation
      const targetRotation = new THREE.Euler().setFromQuaternion(
        targetQuaternion
      );
      console.log("Target rotation:", {
        x: THREE.MathUtils.radToDeg(targetRotation.x).toFixed(2),
        y: THREE.MathUtils.radToDeg(targetRotation.y).toFixed(2),
        z: THREE.MathUtils.radToDeg(targetRotation.z).toFixed(2),
      });

      // Create a proxy object for the entire animation
      const proxy = {
        x: startPos.x,
        y: startPos.y,
        z: startPos.z,
        progress: 0,
      };

      // Log initial state
      const startRotation = new THREE.Euler().setFromQuaternion(
        startQuaternion
      );
      console.log("=== Starting Transition ===");
      console.log("Current camera state:", {
        position: {
          x: startPos.x.toFixed(2),
          y: startPos.y.toFixed(2),
          z: startPos.z.toFixed(2),
        },
        rotation: {
          x: THREE.MathUtils.radToDeg(startRotation.x).toFixed(2),
          y: THREE.MathUtils.radToDeg(startRotation.y).toFixed(2),
          z: THREE.MathUtils.radToDeg(startRotation.z).toFixed(2),
        },
      });

      // Temporarily disable orbit controls during transition
      if (this.controls) {
        this.controls.enabled = false;
      }

      gsap.to(proxy, {
        x: 0,
        y: currentDistance,
        z: 0,
        progress: 1,
        duration: 1.0,
        ease: "power2.inOut",
        onUpdate: () => {
          // Update position
          this.camera.position.set(proxy.x, proxy.y, proxy.z);

          // Interpolate rotation using quaternion slerp
          const tempQuaternion = startQuaternion.clone();
          tempQuaternion.slerp(targetQuaternion, proxy.progress);
          this.camera.quaternion.copy(tempQuaternion);

          // Log current state
          const currentRotation = new THREE.Euler().setFromQuaternion(
            this.camera.quaternion
          );
          console.log("During transition:", {
            progress: proxy.progress.toFixed(2),
            rotation: {
              x: THREE.MathUtils.radToDeg(currentRotation.x).toFixed(2),
              y: THREE.MathUtils.radToDeg(currentRotation.y).toFixed(2),
              z: THREE.MathUtils.radToDeg(currentRotation.z).toFixed(2),
            },
          });
        },
        onComplete: () => {
          // Only update position in onComplete, keep the final interpolated rotation
          this.camera.position.set(0, currentDistance, 0);

          // Re-enable orbit controls if they should be enabled
          if (this.controls) {
            this.controls.enabled = this.orbitControlsEnabled;
            this.controls.update();
          }

          const finalRotation = new THREE.Euler().setFromQuaternion(
            this.camera.quaternion
          );
          console.log("=== Transition Complete ===");
          console.log("Final camera state:", {
            position: {
              x: this.camera.position.x.toFixed(2),
              y: this.camera.position.y.toFixed(2),
              z: this.camera.position.z.toFixed(2),
            },
            rotation: {
              x: THREE.MathUtils.radToDeg(finalRotation.x).toFixed(2),
              y: THREE.MathUtils.radToDeg(finalRotation.y).toFixed(2),
              z: THREE.MathUtils.radToDeg(finalRotation.z).toFixed(2),
            },
          });
        },
      });
    }
  }
}

// Start the game when the DOM is loaded
window.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("game-container");
  if (!container) {
    console.error("Could not find game container element!");
    return;
  }
  const game = new ChakrasGame(container);
  game.animate(); // Start the animation loop
});
